import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

interface VirtualSelectListProps<T> {
  items: T[];
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  renderItem: (item: T, index: number, tabIndex: number, focusIndex: (index: number) => void) => ReactNode;
  resetKey?: unknown;
}

/** Shared measured window for flat options and the expanded, visible rows of a tree. */
export function VirtualSelectList<T>({
  items,
  getKey,
  getLabel,
  renderItem,
  resetKey = undefined,
}: VirtualSelectListProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const pendingFocus = useRef<number | null>(null);
  const typeahead = useRef({ text: '', time: 0 });
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    getItemKey: index => getKey(items[index]),
    estimateSize: () => 48,
    overscan: 4,
    // Menus can scroll while focus is restored during a React commit.
    useFlushSync: false,
    // Keep a focused row mounted even when the user scrolls it outside the window.
    rangeExtractor: range =>
      [...new Set([...defaultRangeExtractor(range), Math.min(activeIndex, items.length - 1)])]
        .filter(index => index >= 0)
        .sort((a, b) => a - b),
  });

  useLayoutEffect(() => {
    virtualizer.scrollToOffset(0);
    setActiveIndex(0);
  }, [resetKey]);

  const focusIndex = (index: number) => {
    const next = Math.max(0, Math.min(index, items.length - 1));
    pendingFocus.current = next;
    setActiveIndex(next);
    virtualizer.scrollToIndex(next, { align: 'auto' });
  };

  useLayoutEffect(() => {
    if (pendingFocus.current === null) return;
    const element = scrollRef.current?.querySelector<HTMLElement>(
      `[data-virtual-index="${pendingFocus.current}"] [data-virtual-focus]`
    );
    if (element) {
      element.focus({ preventScroll: true });
      pendingFocus.current = null;
    }
  });

  return (
    <div
      ref={scrollRef}
      className="min-h-0 overflow-y-auto"
      style={{ maxHeight: 320, overflowAnchor: 'none' }}
      onFocusCapture={event => {
        const row = (event.target as HTMLElement).closest<HTMLElement>('[data-virtual-index]');
        if (row) setActiveIndex(Number(row.dataset.virtualIndex));
      }}
      onKeyDown={event => {
        if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
        const row = (event.target as HTMLElement).closest<HTMLElement>('[data-virtual-index]');
        if (!row) return;
        const index = Number(row.dataset.virtualIndex);
        const next = { ArrowDown: index + 1, ArrowUp: index - 1, Home: 0, End: items.length - 1 }[event.key];
        if (next !== undefined) {
          event.preventDefault();
          event.stopPropagation();
          focusIndex(next);
        } else if (event.key.length === 1 && event.key !== ' ') {
          const now = Date.now();
          const text = now - typeahead.current.time > 500 ? event.key : typeahead.current.text + event.key;
          typeahead.current = { text: text.toLowerCase(), time: now };
          const term = [...text].every(character => character.toLowerCase() === text[0].toLowerCase()) ? text[0] : text;
          for (let offset = 1; offset <= items.length; offset++) {
            const candidate = (index + offset) % items.length;
            if (getLabel(items[candidate]).toLowerCase().startsWith(term.toLowerCase())) {
              event.preventDefault();
              event.stopPropagation();
              focusIndex(candidate);
              break;
            }
          }
        }
      }}
    >
      <div style={{ height: virtualizer.getTotalSize(), position: 'relative', width: '100%' }}>
        {virtualizer.getVirtualItems().map(row => (
          <div
            key={row.key}
            ref={virtualizer.measureElement}
            data-index={row.index}
            data-virtual-index={row.index}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', transform: `translateY(${row.start}px)` }}
          >
            {renderItem(
              items[row.index],
              row.index,
              row.index === Math.min(activeIndex, items.length - 1) ? 0 : -1,
              focusIndex
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
